import { DashboardFilter } from 'types/GlobalDashboardFilter'
import { IUnderlyingDataResponse, PeriodFilter } from 'types/GlobalWidget'
import { TMetricQueryParams } from 'types/GlobalMetric'

import { filterValuesToStrings } from 'utils/metricView'
import { Filters } from 'utils/types'

import { FilterSegmentParams } from './ChartView/ChartElement'
import { TDataDialogColumn } from './types'

type ResolvedPeriod = {
  period_filter: PeriodFilter | null
  from_date: string | null
  to_date: string | null
}

// Merge baseline -> segment -> local filters (later overrides earlier) into a
// plain attribute -> values map. Feeds both the fetch and the chat metric view
// so the two never drift.
//
// `labels` is a parallel attribute -> display-label map (index-aligned with
// `filters`) for attributes whose filter value is a raw id distinct from its
// human-readable name (e.g. a "handläggare" segment). It is display-only —
// never fed into the query — and only has an entry where a label is known;
// callers should fall back to the raw value when one is missing.
export const mergeFilters = (
  baseFilters: TMetricQueryParams['filter'],
  segmentFilters: Filters,
  localFilters: DashboardFilter
): { filters: Record<string, string[]>; labels: Record<string, string[]> } => {
  const filters: Record<string, string[]> = {}
  const labels: Record<string, string[]> = {}

  Object.entries(baseFilters ?? {}).forEach(([key, value]) => {
    filters[key] = value.eq
  })

  Object.entries(segmentFilters).forEach(([key, filter]) => {
    const vals = filterValuesToStrings(filter.value)

    if (vals.length > 0) {
      filters[key] = vals

      // Without display_value there is no label; null would become 'null'.
      const filterLabels =
        filter.display_value == null
          ? []
          : filterValuesToStrings(filter.display_value)

      if (filterLabels.length === vals.length) {
        labels[key] = filterLabels
      } else {
        delete labels[key]
      }
    }
  })

  localFilters.forEach((filter) => {
    if (filter.value !== null) {
      filters[filter.attribute] = [String(filter.value)]
      labels[filter.attribute] = [String(filter.display_value ?? filter.value)]
    }
  })

  return { filters, labels }
}

// Build the details-endpoint query params from the merged filters + resolved
// period.
export const buildQueryParams = (
  mergedFilters: Record<string, string[]>,
  period: ResolvedPeriod
): TMetricQueryParams => {
  const filter: NonNullable<TMetricQueryParams['filter']> = {}

  Object.entries(mergedFilters).forEach(([key, values]) => {
    filter[key] = { eq: values }
  })

  return {
    ...(Object.keys(filter).length > 0 ? { filter } : {}),
    ...(period.period_filter ? { period_filter: period.period_filter } : {}),
    ...(period.from_date ? { from_date: period.from_date } : {}),
    ...(period.to_date ? { to_date: period.to_date } : {})
  }
}

// The ordered ids of the visible (non-hidden) columns.
export const toVisibleColumnIds = (columns: TDataDialogColumn[]): string[] =>
  [...columns]
    .filter((item) => !item.hidden)
    .sort((a, b) => a.index - b.index)
    .map((item) => item.id)

// The default column selection for a details response: every entity column
// visible, in response order.
export const deriveColumnSelection = (
  raw: IUnderlyingDataResponse
): { [columnId: string]: TDataDialogColumn } => {
  const columns: { [columnId: string]: TDataDialogColumn } = {}

  raw.data.forEach((item) => {
    if (item.entity_column_id) {
      columns[item.entity_column_id] = {
        hidden: false,
        id: item.entity_column_id,
        index: -1
      }
    }
  })

  return columns
}

// Add (or replace) a local "add to filters" entry.
export const upsertLocalFilter = (
  localFilters: DashboardFilter,
  value: FilterSegmentParams
): DashboardFilter => [
  ...localFilters.filter((f) => f.attribute !== value.attributeId),
  {
    attribute_option_id: null,
    attribute: value.attributeId,
    value: value.value,
    display_value: value.label
  }
]

// Remove a single local filter by attribute id + value.
export const removeLocalFilter = (
  localFilters: DashboardFilter,
  params: { id: string; value: string }
): DashboardFilter =>
  localFilters.filter(
    (f) => !(f.attribute === params.id && String(f.value) === params.value)
  )
